import { useState, useEffect, useRef, useCallback } from 'react';
import { getStudentDashboard } from '../api/students';
import { STALE_THRESHOLD_MS } from '../constants';
import { trackDashboardViewed } from '../../../utils/posthogEvents';
import { reportUserFacingError } from '../../../utils/errorReporting';
import { useAuth } from '../../../app/providers/AuthContext';
import { mark, notifyBootTimelineReady } from '../../../utils/bootTiming';

// Track once per mount — not on visibility refetches
let dashboardTrackedForStudent = null;

/** @typedef {import('../../../shared/api/types').StudentDashboardResponse} StudentDashboardResponse */

/**
 * Fetches and manages student dashboard data with visibility-based refetching.
 *
 * Phase 3 cold-boot optimization: if AuthContext has a prefetched dashboard
 * from /api/bootstrap (matching this studentId), hydrate from it synchronously
 * instead of firing /api/students/:id/dashboard. Saves 1 Hangzhou RTT on first
 * paint. Older bootstraps (>STALE_THRESHOLD_MS) still trigger a background
 * refetch via the existing visibility handler — no code change needed there.
 *
 * @param {string} studentId
 * @returns {{
 *   dashboard: StudentDashboardResponse | null,
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>
 * }}
 */
export function useDashboardData(studentId) {
  const { consumePrefetchedDashboard } = useAuth();

  // Consume the /api/bootstrap handoff once at mount. Classify the three
  // shapes consumePrefetchedDashboard can return (see AuthContext contract)
  // into a flat { resolved, pending } tuple the effects below can pattern
  // against. StrictMode re-invokes this initializer in dev; the resolved
  // branch is one-shot (AuthContext clears its ref), but the pending branch
  // replays the same promise — safe because promise resolution is immutable.
  const [{ resolved: initialPrefetch, pending: pendingPromise }] = useState(() => {
    const handoff = consumePrefetchedDashboard?.(studentId) ?? null;
    if (handoff?.dashboard) return { resolved: handoff, pending: null };
    if (handoff?.promise) return { resolved: null, pending: handoff.promise };
    return { resolved: null, pending: null };
  });

  // Ref-guarded to fire exactly once per mount. The hook re-renders whenever
  // AuthContext updates (enrollment status, family members, etc.); without
  // this guard every re-render before the boot-timeline flush would push a
  // duplicate 'dashboard_hydrated_from_prefetch' entry into window.__bootMarks.
  const hydrationNotifiedRef = useRef(false);
  if (initialPrefetch && !hydrationNotifiedRef.current) {
    hydrationNotifiedRef.current = true;
    mark('dashboard_hydrated_from_prefetch');
    notifyBootTimelineReady();
  }

  const [dashboard, setDashboard] = useState(initialPrefetch ? initialPrefetch.dashboard : null);
  // Pending bootstrap keeps us loading until the promise settles — the main
  // effect defers to the pending-handoff effect below instead of racing.
  const [loading, setLoading] = useState(!initialPrefetch);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(initialPrefetch ? initialPrefetch.fetchedAt : 0);

  const loadDashboard = useCallback(async () => {
    if (!studentId) return;

    try {
      setLoading(true);
      const data = await getStudentDashboard(studentId);
      setDashboard(data);
      setError(null);
      lastFetchRef.current = Date.now();
    } catch (err) {
      reportUserFacingError(err, { context: 'Load student dashboard (refetch)' });
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  // Fire the dashboard_viewed PostHog event once per (mount × studentId).
  // Guarded by the module-level `dashboardTrackedForStudent` so visibility
  // refetches don't re-track.
  const trackOnce = (data) => {
    if (!studentId || dashboardTrackedForStudent === studentId) return;
    dashboardTrackedForStudent = studentId;
    // Prefer server-provided total so capped todo arrays don't under-report.
    const todoCount = data?.todoAssignmentsCount ?? data?.todoAssignments?.length ?? 0;
    trackDashboardViewed({
      studentId,
      assignmentCount: todoCount + (data?.upcomingAssignments?.length ?? 0),
      completedCount: data?.pastAssignments?.length ?? 0,
      todoCount,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!studentId) return;

      try {
        setLoading(true);
        const data = await getStudentDashboard(studentId);
        if (cancelled) return;
        setDashboard(data);
        setError(null);
        lastFetchRef.current = Date.now();
        notifyBootTimelineReady();
        trackOnce(data);
      } catch (err) {
        if (cancelled) return;
        reportUserFacingError(err, { context: 'Load student dashboard (initial)' });
        setError('Failed to load dashboard');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Refetch when tab becomes visible (if stale)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && studentId) {
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch > STALE_THRESHOLD_MS) {
          fetchData();
        }
      }
    };

    if (initialPrefetch) {
      // Dashboard already hydrated from /api/bootstrap — skip the duplicate
      // fetch. Still fire the view-tracking event once per mount.
      trackOnce(initialPrefetch.dashboard);
    } else if (pendingPromise) {
      // Bootstrap is still in-flight — the pending-handoff effect below
      // awaits it and hydrates (or falls back to fetchData if it fails).
      // Skipping fetchData here is the core race fix: no duplicate request.
    } else {
      fetchData();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [studentId, initialPrefetch, pendingPromise]);

  // Pending-handoff effect: awaits the in-flight bootstrap promise, then
  // either hydrates from its result or falls back to the standard fetch.
  useEffect(() => {
    if (!pendingPromise) return undefined;
    let cancelled = false;

    pendingPromise.then(async (resolved) => {
      if (cancelled) return;
      if (resolved && resolved.dashboard) {
        setDashboard(resolved.dashboard);
        setError(null);
        setLoading(false);
        lastFetchRef.current = resolved.fetchedAt || Date.now();
        if (!hydrationNotifiedRef.current) {
          hydrationNotifiedRef.current = true;
          mark('dashboard_hydrated_from_prefetch');
          notifyBootTimelineReady();
        }
        trackOnce(resolved.dashboard);
        return;
      }
      // Bootstrap failed (401, mismatch, network) — fall back to the
      // standard fetch path. Same code shape as the no-prefetch branch.
      try {
        const data = await getStudentDashboard(studentId);
        if (cancelled) return;
        setDashboard(data);
        setError(null);
        lastFetchRef.current = Date.now();
        notifyBootTimelineReady();
        trackOnce(data);
      } catch (err) {
        if (cancelled) return;
        reportUserFacingError(err, { context: 'Load student dashboard (bootstrap fallback)' });
        setError('Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }).catch((err) => {
      // Defensive: AuthContext's inflight promise is constructed with
      // () => null as its rejection handler, so this branch is unreachable
      // today. Guard anyway so a future refactor that removes the handler
      // doesn't leave users staring at an infinite spinner.
      if (cancelled) return;
      reportUserFacingError(err, { context: 'Load student dashboard (bootstrap handoff)' });
      setError('Failed to load dashboard');
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [studentId, pendingPromise]);

  return { dashboard, loading, error, refetch: loadDashboard };
}

export default useDashboardData;
